import Tooltip from '@material-ui/core/Tooltip';
import GS from '../../grafsync/grafsync';
import Errored from '../Errored';
import RequestSentIcon from '../icons/RequestSentIcon';
import { registrationService } from '../services/security/registrationService';
import Fab from '@material-ui/core/Fab';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import { Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import React from 'react';
import Grid from '@material-ui/core/Grid';
import CancelButton from '../CancelButton';
import AddButton from '../AddButton';
import { globalStyles } from '../style/gstheme';
import { MAX_EMAIL_LENGTH, MAX_PASSWORD_LENGTH, MAX_USERNAME_LENGTH } from '../utils/constants';
import { makeCancelable, validateEmail, validatePassword, validateUsername } from '../utils/utils';
import PrimaryButton from '../PrimaryButton';

const styles = () => {
    return Object.assign({
        fieldEntry: {
            backgroundColor: '#eeeeee',
            padding: 12,
            borderRadius: '4px',
            marginBottom: 12,
            height: 'calc(100% - 24px)'
        }
    }, globalStyles);
};

class AddUserDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            username: "",
            email: "",
            password: "",
            confirmPassword: "",
            legal: false,
            processing: false,
            checkComplete: false,
            isDuplicate: false,
            confirming: false,
            isErrored: null
        };
        this.textInput = React.createRef();
        this.cancellable = null;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    checkStatus = async () => {
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
        try {
            const response = await this.doCheckStatus(this.state.registrationID).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            if (response.status === "https://grafsync.graphifi.com/schema/gs/rejected") {
                this.setState({
                    isDuplicate: true,
                    confirming: false,
                    processing: false
                });
            }
            else if (response.status === "https://grafsync.graphifi.com/schema/gs/failed") {
                this.setState({
                    isDuplicate: false,
                    confirming: false,
                    processing: false,
                    isErrored: {
                        message: "Registration failed for some reason. Please contact support."
                    }
                });
            }
            else if (response.status === "https://grafsync.graphifi.com/schema/gs/accepted") {
                this.setState({
                    confirming: false,
                    checkComplete: true,
                    user: response.user,
                    processing: false
                });
            }
            else {
                this.hcTimeout = setTimeout(() => {
                    this.checkStatus();
                }, 2000);
            }
        }
        catch (e) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            let message = ["I'm afraid we can't sign up this user for some reason."];
            this.setState({
                isErrored: { message },
                confirming: false,
                processing: false
            });
            return;
        }
    };

    signup = async () => {
        this.setState({ processing: true }, async () => {
            const { username, email, password } = this.state;
            try {
                const response = await this.doRegistration(username, email, password).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                if (response.status === "duplicate") {
                    this.setState({
                        isDuplicate: true,
                        processing: false,
                    });
                }
                else {
                    // Now we wait for confirmation
                    this.setState({
                        isDuplicate: false,
                        registrationID: response.registrationID,
                        confirming: true
                    }, () => this.hcTimeout = setTimeout(() => { this.checkStatus(); }, 2000));
                }
            }
            catch (e) {
                if (this.cancellable && this.cancellable.isCancelled()) return;
                let message = ["I'm afraid we can't sign up this user for some reason."];
                this.setState({
                    isErrored: { message },
                    confirming: false,
                    processing: false
                });
            }
        });
    };

    doRegistration(username, email, password) {
        this.cancellable = makeCancelable(registrationService.signup(username, email, password, GS.getSessionToken()));
        return this.cancellable;
    }

    doCheckStatus(id) {
        this.cancellable = makeCancelable(registrationService.checkStatus(id, GS.getSessionToken()));
        return this.cancellable;
    }

    clearError = () => {
        this.setState({ isErrored: null }, () => this.textInput && this.textInput.current && this.textInput.current.focus());
    };

    handleChange = (event) => {
        const t = event.target;
        this.setState({
            [t.name]: t.value,
            isDuplicate: false
        });
    };

    exitAddUser = () => this.setState({
        open: false,
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
    });

    exitAndDisplayUser = () => {
        this.setState({
            open: false,
            checkComplete: false,
            username: "",
            email: "",
            password: "",
            confirmPassword: "",

        });
        this.props.displayUser(this.state.user);
    };

    handleClickOpen = () => {
        this.setState({
            open: true,
            checkComplete: false,
            username: "",
            email: "",
            password: "",
            confirmPassword: ""
        });
    };

    render() {

        const { classes } = this.props;
        const { username, email, password, isErrored, confirmPassword, processing, isDuplicate, confirming, checkComplete } = this.state;
        const validEmail = validateEmail(email);
        const validPassword = validatePassword(password);
        const validUsername = validateUsername(username);
        const disabled = password !== confirmPassword || !validUsername || !validEmail || !validPassword || processing
            || username.length < 3;

        if (isErrored) return <Errored data={isErrored} clearError={this.clearError} />;

        return (
            <React.Fragment>
                <AddButton title="Add a user" onClick={this.handleClickOpen} />
                <Dialog
                    data-test="add-user-dialog"
                    maxWidth="lg"
                    fullWidth
                    classes={{
                        paper: classes.paper,
                        paperWidthLg: classes.largeDialog
                    }}
                    className={classes.dialog}
                    open={this.state.open}
                    aria-labelledby="adduser-dialog-title">
                    <DialogTitle disableTypography className={classes.mainHeading} id="adduser-dialog-title">Add User</DialogTitle>
                    <DialogContent style={{ height: 340 }}>
                        {checkComplete ?
                            <div style={{ width: '100%', textAlign: 'center', marginTop: 30 }}>
                                <Typography data-test="new-username" className={classes.mainHeading}>{username}</Typography>
                                <Typography className={classes.mainHeading}>Accepted</Typography>
                                <div style={{ marginTop: 30 }}>
                                    <div style={{ marginBottom: '12px' }}>
                                        <RequestSentIcon style={{ width: '36px', height: '32px' }} />
                                    </div>
                                </div>
                                <PrimaryButton onClick={this.exitAndDisplayUser}>Finish</PrimaryButton>
                            </div>
                            :
                            <form autoComplete="off">
                                <div>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <div className={classes.fieldEntry}>
                                                <Typography className={classes.loginSubHeading}>Username<Tooltip title="This field is mandatory"><span> *</span></Tooltip></Typography>
                                                <TextField className={classes.loginText}
                                                    autoComplete="username"
                                                    margin="dense"
                                                    id="username"
                                                    name="username"
                                                    disabled={processing}
                                                    value={username}
                                                    inputRef={this.textInput}
                                                    autoFocus={true}
                                                    error={isDuplicate || (username.length > 0 && !validUsername)}
                                                    variant="outlined"
                                                    placeholder="Username (Minimum 3 characters)"
                                                    helperText={isDuplicate ? "Username or email already used." : ""}
                                                    fullWidth
                                                    onChange={this.handleChange}
                                                    InputProps={{
                                                        className: classes.loginInput,
                                                        inputProps: {
                                                            "data-test": "username",
                                                            className: classes.loginInput,
                                                            maxLength: MAX_USERNAME_LENGTH
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <div className={classes.fieldEntry}>
                                                <Typography className={classes.loginSubHeading}>Email<Tooltip title="This field is mandatory"><span> *</span></Tooltip></Typography>
                                                <TextField className={classes.loginText}
                                                    autoComplete="new-password"
                                                    margin="dense"
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    value={email}
                                                    disabled={processing}
                                                    error={isDuplicate || (email.length > 0 && !validEmail)}
                                                    variant="outlined"
                                                    placeholder="Email"
                                                    fullWidth
                                                    onChange={this.handleChange}
                                                    InputProps={{
                                                        className: classes.loginInput,
                                                        inputProps: {
                                                            "data-test": "email",
                                                            className: classes.loginInput,
                                                            maxLength: MAX_EMAIL_LENGTH
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <div className={classes.fieldEntry}>
                                                <Typography className={classes.loginSubHeading} style={{ marginTop: 12 }}>Password<Tooltip title="This field is mandatory"><span> *</span></Tooltip></Typography>
                                                <TextField className={classes.loginText}
                                                    autoComplete="new-password"
                                                    margin="dense"
                                                    id="password"
                                                    name="password"
                                                    type="password"
                                                    disabled={processing}
                                                    placeholder="Password"
                                                    error={password.length > 0 && !validPassword}
                                                    value={password}
                                                    variant="outlined"
                                                    helperText="Minimum 8 characters including lower case, upper case, a number and a special character from !@#$%^&*"
                                                    fullWidth
                                                    onChange={this.handleChange}
                                                    InputProps={{
                                                        classes: {
                                                            root: classes.loginInput
                                                        },
                                                        inputProps: {
                                                            "data-test": "new-password",
                                                            maxLength: MAX_PASSWORD_LENGTH
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <div className={classes.fieldEntry}>
                                                <Typography className={classes.loginSubHeading} style={{ marginTop: 12 }}>Confirm Password<Tooltip title="This field is mandatory"><span> *</span></Tooltip></Typography>
                                                <TextField className={classes.loginText}
                                                    margin="dense"
                                                    id="confirmPassword"
                                                    name="confirmPassword"
                                                    type="password"
                                                    disabled={processing}
                                                    placeholder="Enter password again"
                                                    value={confirmPassword}
                                                    variant="outlined"
                                                    fullWidth
                                                    onChange={this.handleChange}
                                                    helperText={password !== confirmPassword ? "Passwords do not match." : ""}
                                                    InputProps={{
                                                        classes: {
                                                            root: classes.loginInput
                                                        },
                                                        inputProps: {
                                                            "data-test": "confirm-password",
                                                            maxLength: MAX_PASSWORD_LENGTH
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </div>
                            </form>
                        }
                    </DialogContent>
                    {!checkComplete &&
                        <DialogActions className={classes.dialogActions}>
                            <CancelButton disabled={confirming} onClick={this.exitAddUser} />
                            <Fab
                                data-test="signup"
                                disabled={disabled}
                                variant="extended"
                                color="secondary"
                                style={{ width: 120, marginRight: 20 }}
                                className={classes.button}
                                onClick={() => this.signup()}>{
                                    confirming ? "CONFIRMING" : "ADD"
                                }
                            </Fab>
                        </DialogActions>
                    }
                </Dialog>
            </React.Fragment>
        );
    }
}

AddUserDialog.propTypes = {
    classes: PropTypes.object,
    displayUser: PropTypes.func
};

export default withStyles(styles)(AddUserDialog);